import { useEffect, useMemo, useState } from "react";
import { fetchCoupons, fetchCategories, fetchStats } from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import FeaturedApps from "@/components/FeaturedApps";
import EndingSoon from "@/components/EndingSoon";
import DealOfTheDay from "@/components/DealOfTheDay";
import ReferralBanner from "@/components/ReferralBanner";
import FirstVisitPicker from "@/components/FirstVisitPicker";
import { Input } from "@/components/ui/input";
import { Search, TrendingUp, Clock, Sparkles, Percent, MapPin, X } from "lucide-react";
import { iconFor } from "@/lib/categoryIcons";
import { useZip } from "@/context/ZipContext";
import { storeAvailableInZip } from "@/lib/zipStates";
import { useSearchParams } from "react-router-dom";
import useSeo from "@/lib/useSeo";

const SORTS = [
  { key: "trending", label: "TRENDING", Icon: TrendingUp },
  { key: "newest", label: "NEW TODAY", Icon: Sparkles },
  { key: "expiring", label: "EXPIRING", Icon: Clock },
  { key: "discount", label: "DEEPEST", Icon: Percent },
];

export default function Home() {
  const { zip, setAskOpen } = useZip();
  const [searchParams, setSearchParams] = useSearchParams();
  const storeFilter = searchParams.get("store") || "";
  const [coupons, setCoupons] = useState([]);
  const [cats, setCats] = useState([]);
  const [stats, setStats] = useState(null);

  useSeo({
    title: "The Couponer — 14,000+ US promo codes, coupons & daily deals",
    description:
      "The Couponer — a curated almanac of every worthwhile US promo code, printable coupon, and in-store deal. 14,000+ verified offers from 750+ retailers, refreshed daily.",
    canonical: "https://thecouponer.com/",
    keywords:
      "coupons, promo codes, deals, discounts, coupon codes, promo code, sales, cash back, printable coupons, free shipping, deal of the day, US coupons, online coupons",
    jsonLd: [
      {
        "@type": "WebSite",
        "name": "The Couponer",
        "alternateName": "TheCouponer.com",
        "url": "https://thecouponer.com/",
        "description":
          "A curated almanac of every worthwhile US promo code, printable coupon, and in-store deal.",
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://thecouponer.com/?q={search_term_string}",
          },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "Organization",
        "name": "The Couponer",
        "url": "https://thecouponer.com/",
        "logo": "https://thecouponer.com/og-image.png",
        "sameAs": [],
      },
    ],
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("trending");
  const [source, setSource] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const PAGE = 60;

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => {});
    fetchStats().then(setStats).catch(() => {});
  }, []);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setHasMore(true);
    const t = setTimeout(() => {
      fetchCoupons({
        category,
        store: storeFilter || undefined,
        sort,
        q: q || undefined,
        source: source !== "all" ? source : undefined,
        limit: PAGE,
        skip: 0,
      })
        .then((data) => {
          if (!ignore) {
            setCoupons(data);
            setHasMore(data.length === PAGE);
          }
        })
        .finally(() => !ignore && setLoading(false));
    }, 200);
    return () => {
      ignore = true;
      clearTimeout(t);
    };
  }, [category, sort, q, source, storeFilter]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const more = await fetchCoupons({
        category,
        store: storeFilter || undefined,
        sort,
        q: q || undefined,
        source: source !== "all" ? source : undefined,
        limit: PAGE,
        skip: coupons.length,
      });
      setCoupons((prev) => [...prev, ...more]);
      setHasMore(more.length === PAGE);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/refresh`, { method: "POST", credentials: "include" });
      const data = await r.json();
      const { toast } = await import("sonner");
      toast.success(`${data.inserted} new deal${data.inserted === 1 ? "" : "s"} pulled from live sources`);
      const fresh = await fetchCoupons({ category, sort, q: q || undefined, source: source !== "all" ? source : undefined });
      setCoupons(fresh);
      fetchStats().then(setStats);
    } catch {
      const { toast } = await import("sonner");
      toast.error("Refresh failed — try again in a minute");
    } finally {
      setRefreshing(false);
    }
  };

  const [sortedCoupons, featured, rest] = useMemo(() => {
    // ZIP-based prioritization: bump nationwide + local-state stores first,
    // demote regional stores that don't operate in the user's state.
    let list = coupons;
    if (zip) {
      const local = [];
      const other = [];
      for (const c of coupons) {
        if (storeAvailableInZip(c.store, zip)) local.push(c);
        else other.push(c);
      }
      list = [...local, ...other];
    }
    return [list, list[0], list.slice(1)];
  }, [coupons, zip]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-16">
      {/* Masthead */}
      <section
        className="pb-8 mb-8 relative"
        data-testid="masthead"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="relative">
            <div className="text-[10px] tracking-[0.35em] text-[#5c5c5c] mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[#ff6b6b] pulse-dot"></span>
              VOL. 1 · {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }).toUpperCase()} · UNITED STATES
            </div>
            <h1 className="serif text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight">
              <span className="relative inline-block">
                All the coupons.
                <span className="absolute -top-6 -right-8 md:-right-12 float-slow">
                  <span
                    className="inline-block bg-[#f7c873] text-[#2d2a32] px-3 py-1 text-xs tracking-widest font-black -rotate-6 border-2 border-[#2d2a32]"
                    style={{ borderRadius: 6 }}
                  >
                    FREE!
                  </span>
                </span>
              </span>
              <br />
              <span className="italic text-[#ff6b6b] relative">
                None of the noise.
                <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 300 10" preserveAspectRatio="none">
                  <path d="M0 6 Q75 0 150 5 T300 4" stroke="#4ecdc4" strokeWidth="4" fill="none" strokeLinecap="round"/>
                </svg>
              </span>
            </h1>
          </div>
          <div className="hidden md:block" data-testid="stats-panel">
            {stats && (
              <div className="grid grid-cols-3 gap-3">
                <div
                  className="border-2 border-[#2d2a32] p-3 bg-white rounded-xl -rotate-2 shadow-coral"
                >
                  <div className="serif text-3xl leading-none">{stats.total_coupons}</div>
                  <div className="text-[10px] tracking-[0.2em] mt-1">DEALS</div>
                </div>
                <div
                  className="border-2 border-[#2d2a32] p-3 bg-white rounded-xl rotate-1 shadow-teal"
                >
                  <div className="serif text-3xl leading-none">{stats.total_stores}</div>
                  <div className="text-[10px] tracking-[0.2em] mt-1">STORES</div>
                </div>
                <div
                  className="border-2 border-[#2d2a32] p-3 bg-white rounded-xl -rotate-1 shadow-mustard"
                >
                  <div className="serif text-3xl leading-none">{stats.total_categories}</div>
                  <div className="text-[10px] tracking-[0.2em] mt-1">CATEGORIES</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Ticker */}
      <section className="mb-8 overflow-hidden border-y-2 border-[#2d2a32] py-3 bg-[#4ecdc4]">
        <div className="flex whitespace-nowrap ticker gap-8 mono text-xs text-[#2d2a32] font-semibold">
          {[...(coupons.slice(0, 12) || []), ...(coupons.slice(0, 12) || [])].map((c, i) => (
            <span key={`ticker-${c.id}-${i}`} className="flex items-center gap-2">
              <span className="pulse-dot text-[#ff6b6b]">●</span>
              <span className="font-bold">{c.discount_label}</span>
              <span className="opacity-70">@ {c.store}</span>
              <span className="opacity-60">·</span>
            </span>
          ))}
        </div>
      </section>

      {/* Featured Apps */}
      <FeaturedApps />

      {/* Referral CTA */}
      <ReferralBanner />

      {/* Deal of the Day */}
      <DealOfTheDay />

      {/* Ending Soon */}
      <EndingSoon />

      {/* Store filter chip */}
      {storeFilter && (
        <div className="mb-6 flex items-center gap-2" data-testid="store-filter-pill">
          <span className="eyebrow text-[11px] text-[#5c5c5c]">FILTERED BY STORE:</span>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#2d2a32] bg-[#f7c873] rounded-full font-bold text-sm">
            {storeFilter}
            <button
              onClick={() => {
                const sp = new URLSearchParams(searchParams);
                sp.delete("store");
                setSearchParams(sp);
              }}
              data-testid="clear-store-filter"
              aria-label="Clear store filter"
              className="w-5 h-5 rounded-full bg-[#2d2a32] text-white flex items-center justify-center hover:bg-[#ff6b6b]"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        </div>
      )}

      {/* Search + filters */}
      <FirstVisitPicker onPick={setCategory} />
      <section className="mb-8 space-y-4">
        <div className="relative max-w-2xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c5c5c]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="search-input"
            placeholder="Search stores, deals, or brands..."
            className="pl-11 h-14 rounded-full border-2 border-[#2d2a32] bg-white text-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#ff6b6b] shadow-teal"
          />
        </div>

        <div className="flex flex-wrap gap-2 items-center" data-testid="category-chips">
          <button
            onClick={() => setCategory("All")}
            data-testid="cat-all"
            className={`chip inline-flex items-center gap-1.5 ${category === "All" ? "chip-active" : ""}`}
            style={
              category === "All"
                ? { background: "#2d2a32", color: "#fff8ef", borderColor: "#2d2a32" }
                : { background: "#fff2d6", color: "#2d2a32" }
            }
          >
            {(() => {
              const IconEl = iconFor("All");
              return <IconEl className="w-3 h-3" />;
            })()}
            ALL · {stats?.total_coupons || 0}
          </button>
          {cats.map((c) => {
            const IconEl = iconFor(c.name);
            const tint = `var(--cat-${c.name.toLowerCase()}, var(--cat-default))`;
            const active = category === c.name;
            return (
              <button
                key={c.name}
                onClick={() => setCategory(c.name)}
                data-testid={`cat-${c.name.toLowerCase()}`}
                className="chip inline-flex items-center gap-1.5 transition-transform hover:-translate-y-0.5"
                style={
                  active
                    ? {
                        background: tint,
                        color: "var(--ink)",
                        borderColor: "var(--ink)",
                        boxShadow: "3px 3px 0 var(--ink)",
                        transform: "translateY(-1px)",
                      }
                    : {
                        // tinted (not white) but softer than active
                        background: `color-mix(in oklab, ${tint} 55%, #fff8ef)`,
                        color: "var(--ink)",
                        borderColor: "var(--ink)",
                      }
                }
              >
                <IconEl className="w-3 h-3" />
                {c.name.toUpperCase()} · {c.count}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-2 items-center pt-1">
          <span className="text-[10px] tracking-[0.25em] text-[#5c5c5c] mr-1 font-semibold">SORT ·</span>
          {SORTS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setSort(key)}
              data-testid={`sort-${key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.18em] rounded-full border-2 font-semibold ${
                sort === key
                  ? "border-[#ff6b6b] bg-[#ff6b6b] text-white"
                  : "border-[#2d2a32]/20 hover:border-[#2d2a32] bg-white"
              }`}
            >
              <Icon className="w-3 h-3" />
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 items-center pt-1" data-testid="source-filter">
          <span className="text-[10px] tracking-[0.25em] text-[#5c5c5c] mr-1 font-semibold">SOURCE ·</span>
          {[
            { key: "all", label: "EVERYWHERE", tone: "bg-[#2d2a32] text-white" },
            { key: "curated", label: "CURATED", tone: "bg-[#f7c873] text-[#2d2a32]" },
            { key: "reddit", label: "REDDIT", tone: "bg-[#ff6b6b] text-white" },
            { key: "community", label: "COMMUNITY", tone: "bg-[#b6a6de] text-[#2d2a32]" },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setSource(s.key)}
              data-testid={`source-${s.key}`}
              className={`px-3 py-1.5 text-[10px] tracking-[0.18em] rounded-full border-2 font-semibold ${
                source === s.key
                  ? `border-[#2d2a32] ${s.tone}`
                  : "border-[#2d2a32]/20 hover:border-[#2d2a32] bg-white"
              }`}
            >
              {s.label}
            </button>
          ))}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-live-btn"
            className="ml-2 flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.18em] rounded-full border-2 border-[#2d2a32] bg-[#4ecdc4] text-[#2d2a32] font-bold hover:bg-[#2d2a32] hover:text-[#4ecdc4] disabled:opacity-60"
          >
            <span className={`inline-block w-2 h-2 rounded-full bg-[#ff6b6b] ${refreshing ? "pulse-dot" : ""}`}></span>
            {refreshing ? "PULLING..." : "PULL LIVE DEALS"}
          </button>
        </div>
      </section>

      {/* Feed */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="feed-loading">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="border-2 border-dashed border-black/30 h-64 bg-white/50 animate-pulse" />
          ))}
        </div>
      ) : coupons.length === 0 ? (
        <div className="border-2 border-dashed border-black p-12 text-center bg-white" data-testid="empty-state">
          <p className="serif text-3xl mb-2">No deals match your clip.</p>
          <p className="text-xs tracking-wider text-[#5c5c5c]">Try widening your search or picking another category.</p>
        </div>
      ) : (
        <>
          {featured && (
            <section className="mb-8" data-testid="featured-section">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="serif text-3xl tracking-tight">The Front Page</h2>
                <span className="text-[10px] tracking-[0.25em] text-[#5c5c5c]">EDITOR'S PICK</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <CouponCard coupon={featured} index={0} />
                </div>
                <div
                  className="hidden lg:flex flex-col justify-between border-2 border-[#2d2a32] p-6 hard-shadow-sm relative overflow-hidden"
                  style={{ background: "linear-gradient(135deg, #ff6b6b 0%, #f2a93b 50%, #f7c873 100%)" }}
                >
                  <div
                    className="absolute inset-0 opacity-[0.07] pointer-events-none"
                    style={{ background: "repeating-linear-gradient(45deg, #2d2a32 0px, #2d2a32 2px, transparent 2px, transparent 14px)" }}
                  />
                  <div className="relative">
                    <div className="text-[10px] tracking-[0.25em] text-[#2d2a32] font-bold">DEAL OF THE DAY</div>
                    <p className="serif text-2xl leading-snug mt-2 text-[#2d2a32] font-semibold">
                      "{featured.title}" is trending across {featured.store}. Grab it while it lasts.
                    </p>
                  </div>
                  <div className="cut-divider my-4 relative border-[#2d2a32]/40" />
                  <div className="text-xs tracking-wider relative">
                    <div className="text-[#2d2a32]/60 font-semibold">Category</div>
                    <div className="text-[#2d2a32] font-semibold">{featured.category}</div>
                    <div className="text-[#2d2a32]/60 font-semibold mt-2">Type</div>
                    <div className="text-[#2d2a32] font-semibold">{featured.kind.toUpperCase()}</div>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="mt-10" data-testid="feed-grid">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="serif text-3xl tracking-tight">
                {category === "All" ? "The Full Clip" : category}
              </h2>
              <span className="text-[10px] tracking-[0.25em] text-[#5c5c5c]">
                {coupons.length} DEAL{coupons.length !== 1 ? "S" : ""}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((c, i) => (
                <CouponCard key={c.id} coupon={c} index={i + 1} />
              ))}
            </div>
            {hasMore && (
              <div className="mt-10 flex justify-center">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="load-more-btn"
                  className="px-8 py-4 border-2 border-[#2d2a32] bg-white hover:bg-[#f7c873] font-bold tracking-[0.2em] text-sm rounded-full hard-shadow disabled:opacity-60"
                >
                  {loadingMore ? "LOADING MORE DEALS..." : "LOAD MORE DEALS"}
                </button>
              </div>
            )}
            {!hasMore && coupons.length > PAGE && (
              <div className="mt-10 text-center text-xs tracking-[0.3em] text-[#5c5c5c]">
                — END OF THE CLIP —
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
